import type { Schemas } from '@npc/api-client'

import { api } from './api'
import { call } from './errors'
import { t } from '../copy'

/**
 * HTML 快速发布的客户端调用。
 *
 * 类型与路径全部来自 `openapi/openapi.yaml` 的生成物，一处手写的接口形状都没有。
 *
 * # 幂等键不是可选优化
 *
 * 有副作用的路由（开站点、登记版本、上传、直接发布、直接回滚、放出去）**都要 `X-Idempotency-Key`**，
 * 少了就是 400。契约里那段话说得很直白：网络超时之后重试一次就多开一个站点。
 *
 * 所以键在**发起这次操作时生成一次**，重试沿用同一个——这也是为什么它是
 * 参数而不是在这一层随手 `crypto.randomUUID()`：那样每次重试都是一个新键，
 * 幂等就成了摆设，而它失效的样子和正常一模一样。
 */

export type StaticSite = Schemas['StaticSite']
export type StaticSiteVersion = Schemas['StaticSiteVersion']
export type StaticSiteDeployment = Schemas['StaticSiteDeployment']
export type StaticSiteStatus = Schemas['StaticSiteStatus']

/** 项目站走 projectId；公共站挂在账户上，不要造一个隐藏公共项目。 */
export type StaticPublishScope =
  | { kind: 'project'; projectId: string }
  | { kind: 'account' }
export type StaticSiteApproval = Schemas['StaticSiteApproval']
export type StaticSiteApprovalGrant = Schemas['StaticSiteApprovalGrant']
export type StaticSitePreview = Schemas['StaticSitePreview']
export type StaticSiteReceipt = Schemas['StaticSiteReceipt']
export type StaticSiteUploadResult = Schemas['StaticSiteUploadResult']
export type StaticSiteFastPublishResult = Schemas['StaticSiteFastPublishResult']
export type ProjectStaticSiteSummary = Schemas['ProjectStaticSiteSummary']

/** 一次操作一个键。调用方在动手那一刻生成，重试时原样带回来。 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/** 项目主站。每个项目至多一个；不要用列表第一项冒充。 */
export function primaryStaticSite(sites: readonly StaticSite[] | undefined): StaticSite | undefined {
  return sites?.find((site) => site.is_primary === true)
}

export async function listStaticSites(projectId: string): Promise<StaticSite[]> {
  return call('/projects/{projectId}/static-sites', () =>
    api.GET('/projects/{projectId}/static-sites', {
      params: { path: { projectId } },
    }),
  )
}

export function listStaticSitesFor(scope: StaticPublishScope): Promise<StaticSite[]> {
  if (scope.kind === 'account') {
    return call('/static-sites', () => api.GET('/static-sites'))
  }
  return listStaticSites(scope.projectId)
}

export async function getProjectStaticSiteSummary(projectId: string): Promise<ProjectStaticSiteSummary> {
  return call('/projects/{projectId}/static-sites/summary', () =>
    api.GET('/projects/{projectId}/static-sites/summary', {
      params: { path: { projectId } },
    }),
  )
}

export async function createStaticSite(
  projectId: string,
  input: Schemas['StaticSiteCreate'],
  idempotencyKey: string,
): Promise<StaticSite> {
  return call('/projects/{projectId}/static-sites', () =>
    api.POST('/projects/{projectId}/static-sites', {
      params: { path: { projectId }, header: { 'X-Idempotency-Key': idempotencyKey } },
      body: input,
    }),
  )
}

export async function listStaticSiteVersions(
  projectId: string,
  siteId: string,
): Promise<StaticSiteVersion[]> {
  return call('/projects/{projectId}/static-sites/{siteId}/versions', () =>
    api.GET('/projects/{projectId}/static-sites/{siteId}/versions', {
      params: { path: { projectId, siteId } },
    }),
  )
}

export async function listStaticSiteDeployments(
  projectId: string,
  siteId: string,
): Promise<StaticSiteDeployment[]> {
  return call('/projects/{projectId}/static-sites/{siteId}/deployments', () =>
    api.GET('/projects/{projectId}/static-sites/{siteId}/deployments', {
      params: { path: { projectId, siteId } },
    }),
  )
}

export function listStaticSiteVersionsFor(
  scope: StaticPublishScope,
  siteId: string,
): Promise<StaticSiteVersion[]> {
  if (scope.kind === 'account') {
    return call('/static-sites/{siteId}/versions', () =>
      api.GET('/static-sites/{siteId}/versions', {
        params: { path: { siteId } },
      }),
    )
  }
  return listStaticSiteVersions(scope.projectId, siteId)
}

export function listStaticSiteDeploymentsFor(
  scope: StaticPublishScope,
  siteId: string,
): Promise<StaticSiteDeployment[]> {
  if (scope.kind === 'account') {
    return call('/static-sites/{siteId}/deployments', () =>
      api.GET('/static-sites/{siteId}/deployments', {
        params: { path: { siteId } },
      }),
    )
  }
  return listStaticSiteDeployments(scope.projectId, siteId)
}

/**
 * 登录用户的直接发布：拖入 HTML/ZIP，一次请求完成校验、建站或选站、存版本并入队。
 *
 * 不要批准码、理由或二次验证。同站更新传 `siteId`；指定地址传 `slug`；
 * 两个都不传则由内容摘要派生 slug。回滚走 `fastRollbackStaticSite`。
 *
 * `bodySerializer` 与上传那条同一条理由：openapi-fetch 默认 JSON.stringify，
 * 会把网页变成带引号的字符串再发出去。
 */
export async function fastPublishStaticSite(
  projectId: string,
  file: Blob,
  idempotencyKey: string,
  input: { siteId?: string; slug?: string; title?: string; newSite?: boolean } = {},
): Promise<StaticSiteFastPublishResult> {
  const bytes = await readBytes(file)
  return call('/projects/{projectId}/static-sites/fast-publish', () =>
    api.POST('/projects/{projectId}/static-sites/fast-publish', {
      params: {
        path: { projectId },
        header: { 'X-Idempotency-Key': idempotencyKey },
        query: {
          ...(input.newSite ? { new_site: true } : input.siteId ? { site_id: input.siteId } : {}),
          ...(input.slug ? { slug: input.slug } : {}),
          ...(input.title ? { title: input.title } : {}),
        },
      },
      body: bytes as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: { 'Content-Type': 'application/octet-stream' },
    }),
  )
}

/**
 * 登录用户的直接回滚：把站点退到一个已有版本并入队。
 *
 * 不要批准码、理由、`expected_row_version` 或二次验证。CAS 由服务端现读。
 */
export async function fastRollbackStaticSite(
  projectId: string,
  siteId: string,
  versionId: string,
  idempotencyKey: string,
): Promise<StaticSiteFastPublishResult> {
  return call('/projects/{projectId}/static-sites/{siteId}/fast-rollback', () =>
    api.POST('/projects/{projectId}/static-sites/{siteId}/fast-rollback', {
      params: {
        path: { projectId, siteId },
        header: { 'X-Idempotency-Key': idempotencyKey },
      },
      body: { version_id: versionId },
    }),
  )
}

export async function fastPublishStaticSiteFor(
  scope: StaticPublishScope,
  file: Blob,
  idempotencyKey: string,
  input: { siteId?: string; slug?: string; title?: string; newSite?: boolean } = {},
): Promise<StaticSiteFastPublishResult> {
  if (scope.kind === 'project') {
    return fastPublishStaticSite(scope.projectId, file, idempotencyKey, input)
  }
  const bytes = await readBytes(file)
  return call('/static-sites/fast-publish', () =>
    api.POST('/static-sites/fast-publish', {
      params: {
        header: { 'X-Idempotency-Key': idempotencyKey },
        query: {
          ...(input.newSite ? { new_site: true } : input.siteId ? { site_id: input.siteId } : {}),
          ...(input.slug ? { slug: input.slug } : {}),
          ...(input.title ? { title: input.title } : {}),
        },
      },
      body: bytes as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: { 'Content-Type': 'application/octet-stream' },
    }),
  )
}

export async function fastRollbackStaticSiteFor(
  scope: StaticPublishScope,
  siteId: string,
  versionId: string,
  idempotencyKey: string,
): Promise<StaticSiteFastPublishResult> {
  if (scope.kind === 'project') {
    return fastRollbackStaticSite(scope.projectId, siteId, versionId, idempotencyKey)
  }
  return call('/static-sites/{siteId}/fast-rollback', () =>
    api.POST('/static-sites/{siteId}/fast-rollback', {
      params: {
        path: { siteId },
        header: { 'X-Idempotency-Key': idempotencyKey },
      },
      body: { version_id: versionId },
    }),
  )
}

/**
 * 传一份网页上来。
 *
 * 请求体就是**文件本身的原始字节**——不是 multipart，也不要 base64。
 * 服务端自己重算 SHA-256，所以这里不算、也不声明 `manifest_sha256`：
 * 算一遍再传上去，只是给「两边算得不一样」多开一条会 400 的路，
 * 而它挡不住任何东西（服务端本来就不信客户端那个值）。
 *
 * `bodySerializer` 那一行不能省：openapi-fetch 默认把 body 当 JSON 序列化，
 * 于是一个 HTML 文件会被 `JSON.stringify` 成一个带引号的字符串发出去，
 * 而服务端算出来的 SHA-256 是那串引号的——**传上去的不是你选的那个文件**，
 * 而且它会「成功」。
 *
 * 先读成字节再发，而不是把 `File` 直接交给 fetch：那条路在 jsdom 里走不通
 * （undici 拿 jsdom 的 Blob 会抛 `object.stream is not a function`），
 * 也就是说**用例永远验不到它**——一条只有在浏览器里才走得通的路，
 * 等于没有用例守着。服务端上限 5 MiB，整份读进内存是有界的。
 */
export async function uploadStaticSiteVersion(
  projectId: string,
  siteId: string,
  file: Blob,
  idempotencyKey: string,
): Promise<StaticSiteUploadResult> {
  const bytes = await readBytes(file)
  return call('/projects/{projectId}/static-sites/{siteId}/upload', () =>
    api.POST('/projects/{projectId}/static-sites/{siteId}/upload', {
      params: { path: { projectId, siteId }, header: { 'X-Idempotency-Key': idempotencyKey } },
      body: bytes as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: { 'Content-Type': 'application/octet-stream' },
    }),
  )
}

/**
 * 把一个 Blob 读成字节。
 *
 * **走 FileReader，不走 `blob.arrayBuffer()`**，虽然后者短得多。理由是
 * jsdom 的 Blob 上只有 `slice` / `size` / `type` 三样，没有 `arrayBuffer`、
 * 也没有 `stream`。写成「有 arrayBuffer 就用它，没有就退回 FileReader」的话，
 * **生产走的那一支永远没有用例、用例走的那一支永远不在生产跑**——
 * 两侧各自都没被验过，而覆盖率看起来是满的。
 *
 * FileReader 在每个浏览器与 jsdom 里都有，所以这里只有一条路，
 * 而那条路每次跑用例都真的走一遍。
 */
function readBytes(file: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.readAsArrayBuffer(file)
  })
}

/**
 * 把某一版放出去。
 *
 * 两样东西是契约要求的，前端不许自己兜底：
 *
 *   `expected_row_version`  用户**看到的**那个 row_version 原样填回去。
 *                           留空不等于「用最新的」——那样两个人同时发布会互相盖掉，
 *                           而且没人会知道。对不上是 409。
 *   `X-Signed-Action`       黄级动作的平台批准。**没有它就是 403，这是对的**：
 *                           一条能在前端造出来的旁路等于没有门（CLAUDE.md §8）。
 *                           所以这里只负责把用户拿到的那个码原样带上，
 *                           拿不到就让服务端拒绝，不在前端假装成功。
 */
export async function publishStaticSite(
  projectId: string,
  siteId: string,
  input: {
    versionId: string
    expectedRowVersion: number
    reason: string
    signedAction: string
    deploymentType?: Schemas['StaticSiteDeploymentType']
  },
  idempotencyKey: string,
): Promise<StaticSiteDeployment> {
  return call('/projects/{projectId}/static-sites/{siteId}/publish', () =>
    api.POST('/projects/{projectId}/static-sites/{siteId}/publish', {
      params: {
        path: { projectId, siteId },
        header: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Signed-Action': input.signedAction,
        },
      },
      body: {
        version_id: input.versionId,
        expected_row_version: input.expectedRowVersion,
        reason: input.reason,
        ...(input.deploymentType ? { deployment_type: input.deploymentType } : {}),
      },
    }),
  )
}

/**
 * 开一条「等你点一下」。
 *
 * 这是去要那张 `X-Signed-Action` 的**第一步**，不是最后一步：
 * 它只登记「有人想做这件事」，码要等人在
 * `POST /actions/{actionId}/approve` 上按过指纹之后才由服务端签出来。
 *
 * 同一个目标连点三下拿回的是同一条（第二次起服务端回 200 而不是 201），
 * 所以这里不做任何本地去重——**去重的判据在库上那个部分唯一索引里**，
 * 前端再判一遍只会多出一份会漂开的真相。
 */
export async function requestStaticSiteApproval(
  projectId: string,
  siteId: string,
  input: Schemas['StaticSiteApprovalRequest'],
): Promise<StaticSiteApproval> {
  return call('/projects/{projectId}/static-sites/{siteId}/approvals', () =>
    api.POST('/projects/{projectId}/static-sites/{siteId}/approvals', {
      params: { path: { projectId, siteId } },
      body: input,
    }),
  )
}

/**
 * 点头那一下，拿回那张码。
 *
 * `stepUpToken` 是**批红级那一类**的二次验证令牌，而且绑定到这个 `actionId`
 * ——拿别的 intent 或者别条动作换来的令牌会被服务端拒掉。
 * 换令牌那一段（`/auth/step-up/options` → 系统指纹弹窗 → `/auth/step-up/verify`）
 * 在 `app/queries.ts`，与注册 Passkey 那处走同一条路。
 *
 * **这个函数不生成任何签名。** 它把服务端签好的那一串原样交回调用方，
 * 调用方再原样放进 `X-Signed-Action`。浏览器里没有私钥，
 * 也没有一个「帮我签一张」的接口——一条能在前端造出来的旁路等于没有门。
 */
export async function approveStaticSiteAction(
  actionId: string,
  stepUpToken: string,
): Promise<StaticSiteApprovalGrant> {
  const result = await call('/actions/{actionId}/approve', () =>
    api.POST('/actions/{actionId}/approve', {
      params: { path: { actionId }, header: { 'X-Npc-Step-Up': stepUpToken } },
    }),
  )
  // 这条路由回两种形状：机器动作回 `Action`，静态站点回 `StaticSiteApprovalGrant`。
  // 靠 `signed_action` 区分——它在另一个形状里根本不存在。
  // **认不出来就报错，不往下走**：把一个 `Action` 当成批准码送进
  // `X-Signed-Action`，服务端会回 403，而那个 403 跟「没有码」长得一模一样。
  if (!isApprovalGrant(result)) {
    // 这一句走模板，不写死在这儿：它**会上屏**（`PublishForm` 把
    // mutation 的 error.message 交给 `Problem`），而所有面向用户的中文
    // 都由固定模板 + 变量生成（CLAUDE.md §3）。
    throw new Error(t('static_site.publish.approval_not_a_grant'))
  }
  return result
}

function isApprovalGrant(value: unknown): value is StaticSiteApprovalGrant {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { signed_action?: unknown }).signed_action === 'string' &&
    (value as { signed_action: string }).signed_action.length > 0
  )
}

/**
 * `slug` 的规则与服务端那条正则同一份意思：小写字母或数字开头结尾，
 * 中间可以有短横线，最长 63 个字符。
 *
 * 前端这一遍**不是**服务端那道判断的替身，它只是省一趟往返。
 * 服务端的 CHECK 与 validSlug 照旧跑——两边都判是对的，
 * 只在前端判才是错的。
 */
export function validStaticSiteSlug(slug: string): boolean {
  // preview- 是预览地址的保留前缀（服务端 validSlug 与迁移 000085 的 CHECK
  // 一起挡）。这里跟着挡一遍只是为了早点告诉用户，不是替服务端做判断。
  if (slug.startsWith(PREVIEW_HOST_PREFIX)) return false
  return /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(slug)
}

/** 预览地址的保留前缀，与服务端 staticsites.PreviewHostPrefix 同一个值。 */
export const PREVIEW_HOST_PREFIX = 'preview-'

/**
 * 这个站点开过哪些预览链接。
 *
 * **已过期与已关掉的也在里面**：界面要说得出「这条为什么点不开」。
 */
export async function listStaticSitePreviews(
  projectId: string,
  siteId: string,
): Promise<StaticSitePreview[]> {
  return call('/projects/{projectId}/static-sites/{siteId}/previews', () =>
    api.GET('/projects/{projectId}/static-sites/{siteId}/previews', {
      params: { path: { projectId, siteId } },
    }),
  )
}

/** 给某一版再开一条预览链接（上一条过期或被关掉时用）。 */
export async function createStaticSitePreview(
  projectId: string,
  siteId: string,
  versionId: string,
): Promise<StaticSitePreview> {
  return call('/projects/{projectId}/static-sites/{siteId}/versions/{versionId}/previews', () =>
    api.POST('/projects/{projectId}/static-sites/{siteId}/versions/{versionId}/previews', {
      params: { path: { projectId, siteId, versionId } },
    }),
  )
}

/** 提前关掉一条预览链接。重复关同一条不算错。 */
export async function revokeStaticSitePreview(
  projectId: string,
  siteId: string,
  previewId: string,
): Promise<StaticSitePreview> {
  return call('/projects/{projectId}/static-sites/{siteId}/previews/{previewId}/revoke', () =>
    api.POST('/projects/{projectId}/static-sites/{siteId}/previews/{previewId}/revoke', {
      params: { path: { projectId, siteId, previewId } },
    }),
  )
}

/**
 * 稳定网址。内容域由后端配置（`NPC_CONTENT_DOMAIN`），契约里没有这个字段，
 * 所以这里用与后端同一个默认值，并且**只用来显示与打开**——
 * 任何判断都不许依赖它。配错了用户看到的是一个打不开的链接，
 * 而不是一次悄悄发到别处的发布。
 */
export const CONTENT_DOMAIN: string = import.meta.env.VITE_CONTENT_DOMAIN ?? 'static.arronx.ai'

export function staticSiteUrl(slug: string): string {
  return `https://${slug}.${CONTENT_DOMAIN}/`
}

/** 拖放只收网页和 ZIP。后端 `content.Ingest` 认这两种，其它文件会被内容检查挡下。 */
export const STATIC_PUBLISH_FILE_ACCEPT = '.html,.htm,.zip,text/html,application/zip'

/**
 * 直接发布入队后访问者仍看旧版。等 active 才给出 *.static.arronx.ai。
 * pending 期间继续问，已经在线上或失败了就停——不停的话会变成假运行。
 */
export function staticSiteDeploymentsRefetchMs(
  items: readonly Pick<StaticSiteDeployment, 'status'>[] | undefined,
): number | false {
  if (!items?.some((item) => item.status === 'pending')) return false
  return 1500
}

/**
 * 公网地址只在「现在正在服务」时给出来。
 *
 * `site.status=active`（文案「开着」）只表示站点没被关掉，
 * 真正给访问者看的那一版是部署历史里 `status=active` 的那条。
 * 没有这一条就不要拼一个打得开的链接——那会让人以为已经上线了。
 */
export function livePublicUrl(
  slug: string,
  deployments: readonly StaticSiteDeployment[] | undefined,
): string | null {
  if (!deployments?.some((item) => item.status === 'active')) return null
  return staticSiteUrl(slug)
}
